import { ExistingGalleryCard, PhotoSubmissionStatusBadge } from '@/components/photo-submissions';
import {
  photoSubmissionCrudConfig,
  type PhotoSubmission,
} from '@/config/objects/photo-submissions';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CrudDetailView,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PageContainer,
  Textarea,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

/**
 * Photo Submission detail page with review functionality
 */
export function PhotoSubmissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [submission, setSubmission] = useState<PhotoSubmission | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [creditName, setCreditName] = useState('');
  const [galleryOrder, setGalleryOrder] = useState(0);
  const [rejectionReason, setRejectionReason] = useState('');

  // Pre-fill credit name when submission loads
  useEffect(() => {
    if (submission?.creditName) {
      setCreditName(submission.creditName);
    } else if (submission?.wantsCredit && submission?.user) {
      const userName =
        `${submission.user.firstName || ''} ${submission.user.lastName || ''}`.trim();
      setCreditName(userName);
    }
  }, [submission]);

  // Build custom actions for pending submissions
  const customActions = useMemo(() => {
    if (submission?.status !== 'pending') return [];
    return [
      {
        key: 'approve',
        label: 'Approve',
        icon: Check,
        variant: 'default' as const,
        onClick: () => setShowApproveDialog(true),
      },
      {
        key: 'reject',
        label: 'Reject',
        icon: X,
        variant: 'destructive' as const,
        onClick: () => setShowRejectDialog(true),
      },
    ];
  }, [submission?.status]);

  const reviewMutation = useMutation({
    mutationFn: async (data: {
      action: 'approve' | 'reject';
      creditName?: string;
      galleryOrder?: number;
      rejectionReason?: string;
    }) => {
      return apiClient.put(`/photo-submissions/${id}/review`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['PhotoSubmission'] });
      toast.success(
        showApproveDialog ? 'Photo approved and added to gallery' : 'Photo submission rejected',
      );
      setShowApproveDialog(false);
      setShowRejectDialog(false);
      navigate('/photo-submissions');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to review submission');
    },
  });

  const handleApprove = () => {
    reviewMutation.mutate({
      action: 'approve',
      creditName: creditName || undefined,
      galleryOrder,
    });
  };

  const handleReject = () => {
    reviewMutation.mutate({
      action: 'reject',
      rejectionReason: rejectionReason || undefined,
    });
  };

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Submission Not Found</h2>
          <p className="text-muted-foreground">
            The requested photo submission could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Photo Submissions', href: '/photo-submissions' },
    { label: submission?.entityName || 'Submission', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={photoSubmissionCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onData={(data: PhotoSubmission) => setSubmission(data)}
          customActions={customActions}
        />

        {/* Photo Preview Card */}
        {submission?.media?.url && (
          <Card>
            <CardHeader>
              <CardTitle>Submitted Photo</CardTitle>
            </CardHeader>
            <CardContent>
              <img
                src={submission.media.url}
                alt="Submitted photo"
                className="max-w-full max-h-[600px] object-contain rounded-lg"
              />
            </CardContent>
          </Card>
        )}

        {/* Existing gallery for the target entity, so the reviewer can compare
            the submission against what's already published (spot duplicates). */}
        {submission && (
          <ExistingGalleryCard
            entityType={submission.entityType}
            entityId={submission.entityId}
            entityName={submission.entityName}
          />
        )}

        {/* Submission Details Card */}
        {submission && (
          <Card>
            <CardHeader>
              <CardTitle>Submission Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <PhotoSubmissionStatusBadge status={submission.status} />
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Entity</Label>
                  <div className="mt-1">
                    <Link
                      to={`/attractions/${submission.entityId}`}
                      className="text-primary hover:underline"
                    >
                      {submission.entityName || submission.entityId}
                    </Link>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Submitted By</Label>
                  <div className="mt-1">
                    {submission.user
                      ? `${submission.user.firstName || ''} ${submission.user.lastName || ''}`.trim() ||
                        submission.user.email
                      : 'Unknown'}
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Wants Credit</Label>
                  <div className="mt-1">{submission.wantsCredit ? 'Yes' : 'No'}</div>
                </div>
                {submission.creditName && (
                  <div>
                    <Label className="text-muted-foreground">Credit Name</Label>
                    <div className="mt-1">{submission.creditName}</div>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">Submitted At</Label>
                  <div className="mt-1">{new Date(submission.createdAt).toLocaleString()}</div>
                </div>
              </div>

              {submission.status === 'rejected' && submission.rejectionReason && (
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground">Rejection Reason</Label>
                  <div className="mt-1 text-destructive">{submission.rejectionReason}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Approve Photo Submission</DialogTitle>
              <DialogDescription>
                This will add the photo to the attraction&apos;s gallery.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="creditName">Credit Name (optional)</Label>
                <Input
                  id="creditName"
                  value={creditName}
                  onChange={e => setCreditName(e.target.value)}
                  placeholder={
                    submission?.wantsCredit ? 'Enter credit name' : 'User declined credit'
                  }
                />
                <p className="text-sm text-muted-foreground">
                  {submission?.wantsCredit
                    ? 'Attribution shown in gallery (e.g., "Photo by John D.")'
                    : 'User does not want credit - leave empty'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="galleryOrder">Gallery Position</Label>
                <Input
                  id="galleryOrder"
                  type="number"
                  min={0}
                  value={galleryOrder}
                  onChange={e => setGalleryOrder(parseInt(e.target.value) || 0)}
                />
                <p className="text-sm text-muted-foreground">
                  Lower numbers appear first (0 = first position)
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleApprove} disabled={reviewMutation.isPending}>
                {reviewMutation.isPending ? 'Approving...' : 'Approve & Add to Gallery'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Photo Submission</DialogTitle>
              <DialogDescription>
                The user will be able to see this rejection in their submissions list.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="rejectionReason">Reason (optional)</Label>
                <Textarea
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  placeholder="e.g., Blurry photo, not relevant to attraction, inappropriate content"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={reviewMutation.isPending}
              >
                {reviewMutation.isPending ? 'Rejecting...' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PageContainer>
  );
}
